import type {
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
} from "react";

import { Link, type LinkProps } from "react-router-dom";

import { CheckIcon } from "@lucide/icons";

import { cx, styled } from "simplydo/react/styled";
import { Icon, type LucideIconData } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";

import {
  StyledDropdownItem,
  StyledDropdownItemDescription,
} from "./Dropdown.styles";
import type { DropdownItemImage, DropdownItemProps } from "./Dropdown.types";
import { useDropdownContext } from "./DropdownContext";

function resolveImage(image: DropdownItemImage | undefined): ReactNode {
  if (image == null || image === false) {
    return null;
  }
  if (typeof image === "string") {
    return <Image src={image} avatar />;
  }
  if (typeof image === "object" && "src" in image && !("$$typeof" in image)) {
    const { src, avatar, ...rest } = image as {
      src?: string;
      avatar?: boolean;
      [key: string]: unknown;
    };
    return <Image src={src} avatar={avatar ?? true} {...(rest as object)} />;
  }
  return image as ReactNode;
}

export type RenderDropdownItemProps = DropdownItemProps & {
  ref?: Ref<HTMLElement>;
  $dropdownTrigger?: boolean;
  $highlighted?: boolean;
  asLink?: boolean;
  to?: LinkProps["to"];
  href?: string;
  target?: string;
  rel?: string;
};

export const RenderDropdownItem = (
  props: RenderDropdownItemProps,
): ReactElement => {
  const {
    children,
    className,
    description,
    disabled,
    icon,
    image,
    label,
    onClick,
    value,
    style,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    ref,
    $dropdownTrigger,
    $highlighted,
    asLink,
    to,
    href,
    target,
    rel,
    ...rest
  } = props;

  const ctx = useDropdownContext();
  const isLinkItem = Boolean(asLink);
  const isTrigger = Boolean($dropdownTrigger);

  const renderedIcon =
    icon &&
    typeof icon === "object" &&
    !Array.isArray(icon) &&
    "node" in icon &&
    "name" in icon ? (
      <Icon icon={icon as unknown as LucideIconData} />
    ) : icon && typeof icon !== "boolean" && typeof icon !== "string" ? (
      (icon as ReactNode)
    ) : null;

  const handleActivate = (event: MouseEvent<HTMLElement>) => {
    if (disabled || isTrigger) {
      if (disabled) {
        event.preventDefault();
      }
      return;
    }
    event.stopPropagation();
    onClick?.(event, props);
    if (ctx && value !== undefined) {
      ctx.selectOption(event, value);
    } else if (ctx && !ctx.multiple && ctx.closeOnChange) {
      ctx.setOpen(false, event);
    }
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (isTrigger) {
      onClick?.(event, props);
      return;
    }
    if (disabled) {
      event.preventDefault();
      return;
    }
    handleActivate(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled || isTrigger) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      if (!isLinkItem || event.key === " ") {
        event.preventDefault();
      }
      if (isLinkItem && event.key === "Enter") {
        if (ctx && !ctx.multiple && ctx.closeOnChange) {
          ctx.setOpen(false, event as unknown as MouseEvent<HTMLElement>);
        }
        return;
      }
      handleActivate(event as unknown as MouseEvent<HTMLElement>);
    }
  };

  const isSelected =
    ctx && value !== undefined
      ? ctx.multiple
        ? Array.isArray(ctx.value) && ctx.value.includes(value)
        : ctx.value === value
      : false;

  const rootAs = asLink ? (href ? "a" : Link) : "div";
  const linkProps = asLink
    ? href
      ? { href, target, rel }
      : { to, target, rel }
    : {};

  return (
    <StyledDropdownItem
      ref={ref as never}
      as={rootAs}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      className={cx(
        className,
        $highlighted && "is-highlighted",
        isSelected && "is-selected",
        disabled && "is-disabled",
      )}
      style={style}
      $highlighted={Boolean($highlighted)}
      $selectable={ctx?.value !== undefined}
      $disabled={Boolean(disabled)}
      {...(rest as object)}
      {...linkProps}
      {...(ctx
        ? ctx.getItemProps({
            active: Boolean($highlighted),
            selected: Boolean(isSelected),
          })
        : { role: "option", "aria-selected": Boolean(isSelected) })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isSelected ? (
        <Icon
          icon={CheckIcon}
          size="small"
          className="dropdown-check"
          aria-hidden
        />
      ) : null}
      {resolveImage(image)}
      {renderedIcon}
      {label}
      {children}
      {description != null ? (
        <StyledDropdownItemDescription>
          {description}
        </StyledDropdownItemDescription>
      ) : null}
    </StyledDropdownItem>
  );
};

type DropdownItemComponentProps = DropdownItemProps & {
  ref?: Ref<HTMLElement>;
  $dropdownTrigger?: boolean;
  $highlighted?: boolean;
};

const DropdownItemComponent = (
  props: DropdownItemComponentProps,
): ReactElement => <RenderDropdownItem {...props} />;

export const DropdownItem = styled(DropdownItemComponent)``;
